// Shimmering Prism — fragment shader (raymarched KIFS prism).
// GLSL matches the standalone demo (src/newbackground/js/shaders/fragmentShader.js); the camera
// origin/basis (uRo/uUu/uVv/uWw) is precomputed on the CPU each frame and uploaded as uniforms.
// Additive since: a central-solid shape selector (uShape / uIcoComplexity, see mapCentralShape) —
// uShape=0 (Prism) is the original octahedron, so the default output is unchanged.
export default `

  precision mediump float;

  #define RAYMARCH_MAX_STEPS 40
  #define RAYMARCH_MAX_DIST 75.0
  #define RAYMARCH_SURFACE_DIST 0.03
  float shine = 0.0;
  float diamond = 0.0;

  // KIFS rotation matrices, precomputed once per fragment (top of main) and reused across
  // all raymarch steps — they depend only on time + loop index, not on ray position.
  // [0..3] = config A (t = time*0.2), [4..7] = config B (t = time*0.1).
  mat2 kRotXZ[8];
  mat2 kRotXY[8];

  // uniforms
  uniform vec2 resolution;
  uniform float time;
  uniform float shininess;
  uniform float overture;
  uniform float prismSize; // size of the central white octahedron ("prism")
  uniform float uShape;          // central-solid selector: 0 Prism · 1 Cube · 2 Sphere · 3 Icosahedron
  uniform float uIcoComplexity;  // icosahedron facet richness (more planes → rounder / dodeca-like)
  uniform vec3 lightColor;       // per-project glow tint (white = neutral); set on PROJECTS hover
  // Camera origin + basis, precomputed on the CPU each frame (constant for every pixel).
  uniform vec3 uRo;
  uniform vec3 uUu;
  uniform vec3 uVv;
  uniform vec3 uWw;

  // From Inigo Quilez website, thanks!
  float smin(float a, float b, float h) {
      float k = clamp((a-b) / h * 0.5 + 0.5, 0.0, 1.0);
      return mix(a, b, k) - k * (1.0 - k) * h;
  }

  vec3 smin(vec3 a, vec3 b, float h) {
      vec3 k = clamp((a-b) / h * 0.5 + 0.5, 0.0, 1.0);
      return mix(a, b, k) - k * (1.0 - k) * h;
  }

  void precomputeKifs() {
    // Same angles as the old rotateZ(t2*0.5) / rotateZ(t2*0.35), but computed once per
    // fragment (here, before the march) instead of ~50x/pixel inside the raymarch loop.
    // Config A: t = time*0.2, config B: t = time*0.1. mat2(c,-s,s,c) matches rotateZ().
    for (int i = 0; i < 4; i++) {
      float fi = float(i);
      float aXZ_A = (time * 0.2 + fi) * 0.5;
      float aXY_A = (time * 0.2 + fi) * 0.35;
      float aXZ_B = (time * 0.1 + fi) * 0.5;
      float aXY_B = (time * 0.1 + fi) * 0.35;
      kRotXZ[i]     = mat2(cos(aXZ_A), -sin(aXZ_A), sin(aXZ_A), cos(aXZ_A));
      kRotXY[i]     = mat2(cos(aXY_A), -sin(aXY_A), sin(aXY_A), cos(aXY_A));
      kRotXZ[i + 4] = mat2(cos(aXZ_B), -sin(aXZ_B), sin(aXZ_B), cos(aXZ_B));
      kRotXY[i + 4] = mat2(cos(aXY_B), -sin(aXY_B), sin(aXY_B), cos(aXY_B));
    }
  }

  // Two literal-indexed variants so the array index is always (loop index + constant) —
  // a valid constant-index-expression in GLSL ES 1.00. A uses [i], B uses [i + 4].
  vec3 kifsA(vec3 p, float s) {
    float s1 = s + shininess * (1.0 + smoothstep(4.0, 2.0, s) * 2.0);
    for (int i = 0; i < 4; i++) {
      p.xz *= kRotXZ[i];
      p.xy *= kRotXY[i];
      p = smin(p, -p, -4.0);
      p -= s1;
      s1 *= 0.7;
    }
    return p;
  }

  vec3 kifsB(vec3 p, float s) {
    float s1 = s + shininess * (1.0 + smoothstep(4.0, 2.0, s) * 2.0);
    for (int i = 0; i < 4; i++) {
      p.xz *= kRotXZ[i + 4];
      p.xy *= kRotXY[i + 4];
      p = smin(p, -p, -4.0);
      p -= s1;
      s1 *= 0.7;
    }
    return p;
  }
  float sdSphere(vec3 p, float r) {
    return length(p) - r;
  }

  float sdOctahedron( vec3 p, float s) {
    p = abs(p);
    float m = p.x+p.y+p.z-s;
    vec3 q;
         if( 3.0*p.x < m ) q = p.xyz;
    else if( 3.0*p.y < m ) q = p.yzx;
    else if( 3.0*p.z < m ) q = p.zxy;
    else return m*0.57735027;

    float k = clamp(0.5*(q.z-q.y+s),0.0,s);
    return length(vec3(q.x,q.y-s+k,q.z-k));
  }

  float sdBox(vec3 p, vec3 b) {
    vec3 q = abs(p) - b;
    return length(max(q, 0.0)) + min(max(q.x, max(q.y, q.z)), 0.0);
  }

  // Icosahedral "generalized distance function" (hg_sdf style): max of |dot(p, nᵢ)| over the
  // icosahedral symmetry normals, minus the size. uIcoComplexity adds normal-pairs progressively —
  // few planes → coarse/blocky silhouette (hexagon-ish), all planes → many-faceted (dodeca-like).
  float sdIcosahedron(vec3 p, float s, float complexity) {
    float PHI = 1.618033988749895;
    // cube-diagonal planes — the coarse base
    float d = abs(dot(p, normalize(vec3( 1.0, 1.0, 1.0))));
    d = max(d, abs(dot(p, normalize(vec3(-1.0, 1.0, 1.0)))));
    if (complexity > 1.5) {
      d = max(d, abs(dot(p, normalize(vec3( 1.0,-1.0, 1.0)))));
      d = max(d, abs(dot(p, normalize(vec3( 1.0, 1.0,-1.0)))));
    }
    // golden-ratio planes — added progressively → rounder, more-faceted
    if (complexity > 2.5) {
      d = max(d, abs(dot(p, normalize(vec3(0.0, 1.0, PHI+1.0)))));
      d = max(d, abs(dot(p, normalize(vec3(0.0,-1.0, PHI+1.0)))));
    }
    if (complexity > 3.5) {
      d = max(d, abs(dot(p, normalize(vec3( PHI+1.0, 0.0, 1.0)))));
      d = max(d, abs(dot(p, normalize(vec3(-PHI-1.0, 0.0, 1.0)))));
    }
    if (complexity > 4.5) {
      d = max(d, abs(dot(p, normalize(vec3( 1.0, PHI+1.0, 0.0)))));
      d = max(d, abs(dot(p, normalize(vec3(-1.0, PHI+1.0, 0.0)))));
    }
    return d - s;
  }

  // Central solid dispatch — Prism (0) is the original octahedron, so uShape=0 is unchanged.
  float mapCentralShape(vec3 p, float s) {
    if (uShape < 0.5)      return sdOctahedron(p, s);
    else if (uShape < 1.5) return sdBox(p, vec3(s));
    else if (uShape < 2.5) return sdSphere(p, s);
    else                   return sdIcosahedron(p, s, uIcoComplexity);
  }

  float getDist(vec3 p) {

    vec3 p2 = kifsA(p, overture);
    vec3 p3 = kifsB(p + vec3(3.0, 2.0, 1.0), overture);

    float d1 = sdSphere(p2, 1.5);
    float d2 = sdSphere(p3, 1.4);

    float dl1 = smin(d1, d2, -1.0);

    float o = mapCentralShape(p, prismSize);
    float sh = 0.1 + shininess;
    diamond += sh / (sh + o);
    dl1 = smin(dl1, o, 0.25);

    float s = 4.0 / (4.0 + dl1);
    shine += s;

    return dl1;

  }

  float rayMarch(vec3 ro, vec3 rd) {
      float dO = RAYMARCH_SURFACE_DIST;

      for (int i = 0; i < RAYMARCH_MAX_STEPS; i++) {
          vec3 p = ro + rd * dO;
          float dS = getDist(p);
          dO += dS;
          if (dO > RAYMARCH_MAX_DIST) break;
          if (dS < RAYMARCH_SURFACE_DIST) break;
      }

      return dO;
  }

  // Lighting path (getNormal / getLight / czm_saturation) removed: its result (dif)
  // was never used. getNormal's 4 getDist calls did contribute a little to the
  // shine/diamond glow accumulators, so peaks may be marginally dimmer (verify A/B).

  void main() {

    // Precompute the KIFS rotation matrices once per fragment, before the raymarch loop
    // (they were previously recomputed ~50x/pixel inside it).
    precomputeKifs();

    vec2 uv = (gl_FragCoord.xy - 0.5 * resolution.xy) / resolution.y;

    // Camera origin (uRo) and basis (uUu/uVv/uWw) are precomputed on the CPU each frame.
    vec3 rd = normalize(uv.x * uUu + uv.y * uVv + uWw * 0.6);

    // rayMarch accumulates the glow (shine/diamond) through getDist's side effects;
    // the returned distance is unused now that the dead lighting path is removed.
    rayMarch(uRo, rd);

    vec3 col = vec3(0.0);

    vec3 orbColor = vec3(0.1);
    // lightColor tints the glow toward the shown project's color (white = neutral, identical to before).
    col += shine * orbColor * lightColor * 0.2 * shininess;
    col += diamond * lightColor * 0.4 * shininess;

    gl_FragColor = vec4(col, 1.0);

  }


`;
